import { DynamoDBClient } from '@aws-sdk/client-dynamodb';
import {
  DynamoDBDocumentClient,
  ScanCommand,
  GetCommand,
} from '@aws-sdk/lib-dynamodb';
import { customers, Customer, getCustomerByCase } from '../data/mockData';
import { format } from 'date-fns';

// Initialize DynamoDB client
const createDynamoDBClient = (region: string, credentials?: { accessKeyId: string; secretAccessKey: string }) => {
  const clientConfig: any = {
    region,
  };

  if (credentials?.accessKeyId && credentials?.secretAccessKey) {
    clientConfig.credentials = credentials;
  }

  const client = new DynamoDBClient(clientConfig);
  return DynamoDBDocumentClient.from(client);
};

// Fetch all customers from DynamoDB
export const fetchCustomersFromDynamoDB = async (
  region: string, 
  tableName: string,
  credentials?: { accessKeyId: string; secretAccessKey: string }
): Promise<Customer[]> => {
  try {
    const docClient = createDynamoDBClient(region, credentials);
    
    const command = new ScanCommand({
      TableName: tableName,
    });
    
    const response = await docClient.send(command);
    
    if (!response.Items) {
      throw new Error('No items returned from DynamoDB');
    }
    
    // Transform DynamoDB items to our Customer type
    return response.Items.map((item: any) => ({
      id: item.id,
      caseId: item.caseId,
      caseStatus: item.caseStatus,
      name: item.name,
      email: item.email,
      phoneNumber: item.phoneNumber,
      requiredDocuments: item.requiredDocuments || [],
      optionalDocuments: item.optionalDocuments || [],
      uploadedDocuments: item.uploadedDocuments || [],
      lastUploadDate: item.lastUploadDate || null,
      lastReminderSent: item.lastReminderSent || null,
    }));
  } catch (error) {
    console.error('Error fetching data from DynamoDB:', error);
    // Fall back to mock data in case of error
    return customers;
  }
};

// Fetch a specific customer by case ID from DynamoDB
export const fetchCustomerByCaseFromDynamoDB = async (
  caseId: string,
  region: string,
  tableName: string,
  credentials?: { accessKeyId: string; secretAccessKey: string }
): Promise<Customer | undefined> => {
  try {
    const docClient = createDynamoDBClient(region, credentials);
    
    // Note: This assumes your DynamoDB table has a GSI on caseId
    // If not, you would need to use a scan with a filter expression
    const command = new GetCommand({
      TableName: tableName,
      Key: {
        caseId: caseId,
      },
    });
    
    const response = await docClient.send(command);
    
    if (!response.Item) {
      return undefined;
    }
    
    const item = response.Item;
    return {
      id: item.id,
      caseId: item.caseId,
      caseStatus: item.caseStatus,
      name: item.name,
      email: item.email || '',
      phoneNumber: item.phoneNumber,
      requiredDocuments: item.requiredDocuments || [],
      optionalDocuments: item.optionalDocuments || [],
      uploadedDocuments: item.uploadedDocuments || [],
      lastUploadDate: item.lastUploadDate || null,
      lastReminderSent: item.lastReminderSent || null,
    };
  } catch (error) {
    console.error('Error fetching customer from DynamoDB:', error);
    // Fall back to mock data in case of error
    return getCustomerByCase(caseId);
  }
};

// Simulate network latency for realistic loading behavior
const simulateNetworkDelay = async (minDelay = 250, maxDelay = 450) => {
  const delay = Math.floor(Math.random() * (maxDelay - minDelay + 1)) + minDelay;
  await new Promise(resolve => setTimeout(resolve, delay));
};

// Main service function that will choose between local and DynamoDB data
export const getCustomers = async (
  useAwsDynamoDB: boolean,
  awsConfig: {
    region: string;
    tableName: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  }
): Promise<Customer[]> => {
  // Simulate network delay for more realistic loading behavior
  await simulateNetworkDelay();
  
  if (useAwsDynamoDB) {
    const credentials = awsConfig.accessKeyId && awsConfig.secretAccessKey
      ? {
          accessKeyId: awsConfig.accessKeyId,
          secretAccessKey: awsConfig.secretAccessKey,
        }
      : undefined;
      
    return fetchCustomersFromDynamoDB(
      awsConfig.region,
      awsConfig.tableName,
      credentials
    );
  }
  
  // Use local mock data
  return Promise.resolve(customers);
};

// Get a specific customer by case ID
export const getCustomerByID = async (
  caseId: string,
  useAwsDynamoDB: boolean,
  awsConfig: {
    region: string;
    tableName: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  }
): Promise<Customer | undefined> => {
  // Simulate network delay
  await simulateNetworkDelay(300, 1000);
  
  if (useAwsDynamoDB) {
    const credentials = awsConfig.accessKeyId && awsConfig.secretAccessKey
      ? {
          accessKeyId: awsConfig.accessKeyId,
          secretAccessKey: awsConfig.secretAccessKey,
        }
      : undefined;
      
    return fetchCustomerByCaseFromDynamoDB(
      caseId,
      awsConfig.region,
      awsConfig.tableName,
      credentials
    );
  }
  
  // Use local mock data
  return Promise.resolve(getCustomerByCase(caseId));
};

// Send document reminder email (simulation)
export const sendDocumentReminder = async (customer: Customer, isDemoMode: boolean = false): Promise<Customer> => {
  // In a real application, this would call an API to send an email
  if (isDemoMode) {
    console.log(`[DEMO MODE] Would have sent reminder email to ${customer.name} (${customer.caseId}), but no email was actually sent.`);
  } else {
    console.log(`Sending reminder email to ${customer.name} (${customer.caseId})`);
  }
  
  // Simulate API delay
  await simulateNetworkDelay(800, 1500);
  
  // Update the lastReminderSent timestamp
  const updatedCustomer = {
    ...customer,
    lastReminderSent: format(new Date(), 'yyyy-MM-dd HH:mm:ss')
  };
  
  // In local storage mode, update the customer in the array
  const index = customers.findIndex(c => c.id === customer.id);
  if (index !== -1) {
    customers[index] = updatedCustomer;
  }
  
  return updatedCustomer;
};

// Check if a reminder can be sent (not sent in the last 7 days)
export const canSendReminder = (customer: Customer): boolean => {
  // If all documents are completed, no need for a reminder
  const completedRequired = customer.requiredDocuments.filter(req => 
    customer.uploadedDocuments.some(doc => doc.requirementId === req.id)
  ).length;
  
  if (completedRequired === customer.requiredDocuments.length) {
    return false;
  }
  
  // If no reminder has been sent yet, can send one
  if (!customer.lastReminderSent) {
    return true;
  }
  
  // Check if the last reminder was more than 7 days ago
  const lastReminderDate = new Date(customer.lastReminderSent);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - lastReminderDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  return diffDays > 7;
};
